import React, { useCallback, useState } from 'react';

import { getPromoCampaignBenefitLabel } from '~/lib/benefits/promoCampaignBenefits';
import dayjs from '~/timeInit';

import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';

import { useDeletePromoCampaign } from '~/hooks/apis/promoCampaigns';

import { luxLoyaltyRegions } from '~/services/LuxLoyaltyService';

import { type PromoCampaignPresenter } from '~/types/loyaltyContract';

interface PromoCampaignDetailsDialogProps {
  open: boolean;
  campaign: PromoCampaignPresenter | null;
  onClose: (deleted?: boolean) => void;
}

export default function PromoCampaignDetailsDialog({ open, campaign, onClose }: PromoCampaignDetailsDialogProps) {
  const deleteMutation = useDeletePromoCampaign();

  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  const handleConfirmDelete = useCallback(async () => {
    if (!campaign) {
      return;
    }

    await deleteMutation.mutateAsync({ promoCampaignId: campaign.id });
    setIsDeleteConfirmOpen(false);
    onClose(true);
  }, [campaign, deleteMutation, onClose]);

  if (!campaign) {
    return null;
  }

  const isDeleted = Boolean(campaign.deletedAt);

  return (
    <>
      <Dialog open={open} onClose={() => onClose()} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h6" component="span">
              Promo Campaign Details
            </Typography>
            {isDeleted && <Chip label="Deleted" size="small" color="error" />}
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Name
              </Typography>
              <Typography>{campaign.name}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                ID
              </Typography>
              <Typography>{campaign.id}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Region
              </Typography>
              <Typography>{luxLoyaltyRegions[campaign.region]}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Benefit
              </Typography>
              <Typography>{getPromoCampaignBenefitLabel(campaign.benefit)}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Tiers
              </Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {campaign.tiers && campaign.tiers.length > 0 ? (
                  campaign.tiers.map((tier) => (
                    <Chip
                      key={tier}
                      label={tier.charAt(0).toUpperCase() + tier.slice(1)}
                      size="small"
                      variant="outlined"
                    />
                  ))
                ) : (
                  <Typography color="text.secondary">None</Typography>
                )}
              </Stack>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Quantity
              </Typography>
              <Typography>{campaign.quantity}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Assigned Users
              </Typography>
              <Typography>{campaign.assignedUsersCount}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Start Date
              </Typography>
              <Typography>{dayjs(campaign.startDate).format('DD/MM/YYYY')}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                End Date
              </Typography>
              <Typography>{dayjs(campaign.endDate).format('DD/MM/YYYY')}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Created At
              </Typography>
              <Typography>{dayjs(campaign.createdAt).format('DD/MM/YYYY HH:mm')}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Updated At
              </Typography>
              <Typography>{dayjs(campaign.updatedAt).format('DD/MM/YYYY HH:mm')}</Typography>
            </Box>
            {campaign.deletedAt && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Deleted At
                </Typography>
                <Typography color="error">{dayjs(campaign.deletedAt).format('DD/MM/YYYY HH:mm')}</Typography>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          {!isDeleted && (
            <Button variant="text" color="error" onClick={() => setIsDeleteConfirmOpen(true)}>
              Delete
            </Button>
          )}
          <Box sx={{ flex: 1 }} />
          <Button variant="text" onClick={() => onClose()}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={isDeleteConfirmOpen} onClose={() => setIsDeleteConfirmOpen(false)}>
        <DialogTitle>Delete Campaign</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the campaign "{campaign.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setIsDeleteConfirmOpen(false)} disabled={deleteMutation.isPending}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleConfirmDelete} disabled={deleteMutation.isPending}>
            {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
